import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CircleDollarSign, ExternalLink, Banknote, Zap, AlertTriangle } from "lucide-react";
import { StuckPayoutDialog } from "@/components/StuckPayoutDialog";
import { ReconciliationQueue } from "@/components/ReconciliationQueue";
import { OfflinePaymentsPanel } from "@/components/OfflinePaymentsPanel";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  pending_release: "bg-[#EFEADF] text-[#1A1A1A] border-[#E8E2D9]",
  released: "bg-[#8A9A86]/15 text-[color:var(--verified)] border-[#8A9A86]/30",
  released_manual: "bg-[#8A9A86]/10 text-[color:var(--verified)] border-[#8A9A86]/30",
  refunded: "bg-[#D9583A]/10 text-[#D9583A] border-[#D9583A]/30",
  partially_refunded: "bg-[#F3E3C3] text-[#7A4A12] border-[#E0B96A]",
};

// Refund-hold state is server-driven (booking.refund_status / payout_status come back on each row).
const HELD_REFUND = { review_required: "Held — refund review", partially_refunded: "Held — partially refunded", refunded: "Blocked — refunded" };
const HELD_PAYOUT_STATUSES = new Set(["held_refund_review", "blocked_refunded", "held"]);
function holdState(r) {
  const rs = r.refund_status || "none";
  if (HELD_REFUND[rs]) return { held: true, label: HELD_REFUND[rs], blocked: rs === "refunded", tone: rs === "refunded" ? STATUS_TONE.refunded : STATUS_TONE.partially_refunded };
  if (HELD_PAYOUT_STATUSES.has(r.payout_status)) return { held: true, label: r.payout_status === "blocked_refunded" ? "Blocked — refunded" : "Held — payout on hold", blocked: r.payout_status === "blocked_refunded", tone: STATUS_TONE.partially_refunded };
  return { held: false };
}

const formatUSD = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } };
// What the admin can actually move: the server-computed releasable share while pending, the amount that
// already moved once released. `payout_amount` is the frozen checkout-time figure and is shown only as context.
const displayAmount = (r) => {
  if (["released", "released_manual"].includes(r.status)) return (Number(r.transferred_cents) || 0) / 100 || Number(r.payout_amount) || 0;
  if (["pending_release", "partially_refunded"].includes(r.status)) return (Number(r.releasable_cents) || 0) / 100;
  return 0;
};

export default function AdminPayouts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_release");
  const [active, setActive] = useState(null);
  const [method, setMethod] = useState("connect");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [stuckRow, setStuckRow] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const { refresh } = useAuth();

  const load = () => {
    setLoading(true);
    const q = search.trim();
    api.get("/admin/payouts", { params: q ? { q } : { status: statusFilter } })
      .then(({ data }) => { setRows(data); setLoadError(null); })
      .catch((e) => {
        setRows([]);
        const code = e?.response?.status;
        setLoadError(code === 401 || code === 403
          ? "This tab is no longer signed in as an admin (another sign-in may have replaced it). Sign in as admin again to see payouts."
          : `Couldn't load payouts: ${formatApiError(e?.response?.data?.detail) || "network error"}`);
        if (code === 401 || code === 403) refresh();
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, search.trim() ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  const open = (row) => {
    setActive(row);
    setMethod(row.vendor_connect_ready ? "connect" : "manual");
    setReference("");
    setNote("");
  };
  const close = () => { setActive(null); setReference(""); setNote(""); };

  const submit = async () => {
    if (!active) return;
    if (method === "manual" && !reference.trim()) {
      return toast.error("Payment reference is required for manual releases (bank transaction ID, ACH confirmation, or check number)");
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/payouts/${active.earning_id}/release`, { method, reference, note });
      toast.success(`Payout ${method === "connect" ? "released via Stripe Connect" : "released manually"}`);
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Release failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-payouts">
      <ReconciliationQueue />
      <OfflinePaymentsPanel />
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="eyebrow">Payout queue</div>
          <div className="text-sm text-[color:var(--text-muted)] mt-1">
            Vendor earnings awaiting release. Use Connect where available; use manual when the vendor isn't Connect-ready.
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {["pending_release", "released", "released_manual", "all"].map((s) => (
            <button
              key={s}
              data-testid={`payout-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`chip ${statusFilter === s ? "active" : ""} ${search.trim() ? "opacity-50" : ""}`}
            >{s.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <Input
          data-testid="payout-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search booking reference, payment reference, vendor, planner…"
          className="bg-white h-11 max-w-xl"
        />
        {search.trim() && (
          <div className="text-[11px] text-[color:var(--text-muted)] mt-1" data-testid="payout-search-hint">
            Searching across all payout states · <button className="link-underline" data-testid="payout-search-clear" onClick={() => setSearch("")}>Clear</button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[color:var(--text-muted)]">Loading payouts…</div>
      ) : loadError ? (
        <div className="card-elegant p-8 text-center text-[#D9583A] space-y-3" data-testid="admin-payouts-error">
          <p>{loadError}</p>
          <Button variant="outline" onClick={load} data-testid="admin-payouts-retry-btn">Retry</Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card-elegant p-8 text-center text-[#5C5C5C]" data-testid="admin-payouts-empty">
          No payouts in this state.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const payoutTotal = displayAmount(r);
            const original = Number(r.payout_amount) || 0;
            const adjusted = Math.abs(payoutTotal - original) > 0.005;
            const badge = STATUS_TONE[r.status] || "bg-[#F5F2EB] text-[#5C5C5C] border-[#E8E2D9]";
            const hold = holdState(r);
            const releasable = r.status === "pending_release" && !hold.held;
            return (
              <div
                key={r.earning_id}
                data-testid={`payout-row-${r.earning_id}`}
                className="card-elegant p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${badge}`} data-testid={`earning-status-${r.earning_id}`}>
                      {r.status.replace("_", " ")}
                    </span>
                    {hold.held && (
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${hold.tone}`} data-testid={`hold-badge-${r.earning_id}`}>
                        {hold.label}
                      </span>
                    )}
                    <span className="text-[11px] text-[color:var(--text-muted)]" data-testid={`processed-via-${r.earning_id}`} title="How this payment was processed">
                      {r.processed_via === "connect" ? "Paid via Connect" : "Platform charge · manual payout"}
                    </span>
                  </div>
                  <div className="font-serif text-xl mt-2">{r.vendor_name} × {r.planner_name}</div>
                  <div className="text-xs text-[color:var(--text-muted)] mt-1">
                    Event {formatDate(r.event_date)} · Booking status {r.booking_status || "—"} · Earning created {formatDate(r.created_at)}
                  </div>
                  <div className="text-[11px] mt-2 text-[color:var(--text-muted)] space-y-0.5">
                    <div data-testid={`booking-ref-${r.earning_id}`}>Booking reference: <span className="font-mono text-[#1A1A1A]">{r.booking_id}</span></div>
                    {r.status === "released_manual" && r.release_reference && (
                      <div data-testid={`payment-ref-${r.earning_id}`}>Payment reference: <span className="font-mono text-[#1A1A1A]">{r.release_reference}</span></div>
                    )}
                    {r.stripe_transfer_id && (
                      <div data-testid={`stripe-transfer-${r.earning_id}`}>Stripe transfer: <span className="font-mono text-[#1A1A1A]">{r.stripe_transfer_id}</span></div>
                    )}
                  </div>
                </div>
                <div className="text-right sm:min-w-[220px]">
                  <div className="eyebrow" data-testid={`payout-amount-label-${r.earning_id}`}>{hold.held ? (hold.blocked ? "Blocked payout" : "Held payout") : ["released", "released_manual"].includes(r.status) ? "Paid out" : "Releasable"}</div>
                  <div className={`font-serif text-2xl mt-1 ${hold.held ? "text-[color:var(--text-muted)]" : ""}`} data-testid={`payout-amount-${r.earning_id}`}>{formatUSD(payoutTotal)}</div>
                  {adjusted && (
                    <div className="text-[11px] text-[color:var(--text-muted)] mt-1" data-testid={`payout-original-${r.earning_id}`}>
                      of {formatUSD(original)} at checkout
                      {r.overpayment_refunded_cents > 0 ? ` · ${formatUSD(r.overpayment_refunded_cents / 100)} duplicate payment refunded` : r.amount_refunded > 0 ? ` · ${formatUSD(r.amount_refunded)} refunded` : ""}
                    </div>
                  )}
                  {hold.held && (
                    <div className="text-[11px] text-[color:var(--text-muted)] mt-1" data-testid={`hold-note-${r.earning_id}`}>
                      {hold.blocked ? "Not releasable — booking refunded." : "Not releasable until the refund review is resolved."}
                    </div>
                  )}
                  {r.stuck_payout_op && (
                    <Button
                      data-testid={`resolve-stuck-btn-${r.earning_id}`}
                      onClick={() => setStuckRow(r)}
                      variant="outline"
                      className="rounded-full h-10 mt-3 w-full"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" /> Resolve stuck payout
                    </Button>
                  )}
                  {releasable && !r.stuck_payout_op && (
                    <Button
                      data-testid={`release-btn-${r.earning_id}`}
                      onClick={() => open(r)}
                      className="btn-sage rounded-full h-10 mt-3 w-full"
                    >
                      <CircleDollarSign className="w-4 h-4 mr-2" /> Release
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StuckPayoutDialog row={stuckRow} onClose={() => setStuckRow(null)} onResolved={() => { setStuckRow(null); load(); }} />

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent data-testid="release-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Release payout</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4 text-sm">
              <div className="rounded-md bg-[#F5F2EB] p-4 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Vendor</span><span>{active.vendor_name}</span></div>
                <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Booking</span><span>{active.booking_id}</span></div>
                <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Amount to release</span><span className="font-semibold" data-testid="release-dialog-amount">{formatUSD(displayAmount(active))}</span></div>
                <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Vendor Connect-ready</span><span>{active.vendor_connect_ready ? "Yes" : "No"}</span></div>
              </div>

              <div>
                <label className="eyebrow block mb-2">Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="method-connect"
                    disabled={!active.vendor_connect_ready}
                    onClick={() => setMethod("connect")}
                    className={`p-3 rounded-md border text-left ${method === "connect" ? "border-[color:var(--accent)] bg-white" : "border-[color:var(--border)]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <Zap className="w-4 h-4 mb-1" />
                    <div className="text-sm font-semibold">Stripe Connect</div>
                    <div className="text-[11px] text-[color:var(--text-muted)]">Direct transfer to vendor</div>
                  </button>
                  <button
                    type="button"
                    data-testid="method-manual"
                    onClick={() => setMethod("manual")}
                    className={`p-3 rounded-md border text-left ${method === "manual" ? "border-[color:var(--accent)] bg-white" : "border-[color:var(--border)]"}`}
                  >
                    <Banknote className="w-4 h-4 mb-1" />
                    <div className="text-sm font-semibold">Manual</div>
                    <div className="text-[11px] text-[color:var(--text-muted)]">Off-Stripe (audited)</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-2" htmlFor="release-ref">
                  {method === "manual" ? "Payment reference (required)" : "Reference (optional)"}
                </label>
                <Input
                  id="release-ref"
                  data-testid="release-reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={method === "manual" ? "Bank transaction ID, ACH confirmation, or check number" : "Optional note"}
                  className="bg-white h-11"
                />
                {method === "manual" && (
                  <p className="text-[11px] text-[color:var(--text-muted)] mt-1.5" data-testid="release-reference-help">
                    Bank transaction ID, ACH confirmation, or check number. Never enter EIN, SSN, bank account, routing, or other sensitive information.
                  </p>
                )}
              </div>

              <div>
                <label className="eyebrow block mb-2" htmlFor="release-note">Note (optional)</label>
                <Textarea
                  id="release-note"
                  data-testid="release-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="bg-white"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close} disabled={submitting} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-release-btn"
              onClick={submit}
              disabled={submitting}
              className="rounded-full bg-[#1A1A1A] hover:bg-black text-white"
            >
              {submitting ? "Releasing…" : "Confirm release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
